import { useEffect, useState } from "react";
import { FolderOpen, Loader2, Archive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { previewLegacyShells, createLegacyShells } from "@/lib/api";

/** After a OneDrive sync: customer folders that match no case, with a
 *  one-click "turn them into closed/archive cases" so their documents
 *  land on a file. */
export function UnmatchedFoldersPanel({ region = "UK", onCreated }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    previewLegacyShells(region).then(setPreview).catch(() => setPreview({ folders: [], count: 0 }));
  }, [region]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await createLegacyShells(region);
      toast.success(`${r.created} archive case${r.created === 1 ? "" : "s"} created · ${r.files_attached} files attached`);
      setPreview(await previewLegacyShells(region));
      onCreated?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create archive cases");
    } finally { setBusy(false); }
  };

  if (!preview) return <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Checking unmatched folders…</div>;
  if (!preview.count) return null;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded p-3 space-y-2" data-testid="unmatched-folders-panel">
      <p className="text-xs font-semibold text-amber-900 flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" />{preview.count} OneDrive customer folder{preview.count === 1 ? "" : "s"} with no {region} case</p>
      <ul className="text-[11px] text-amber-900 max-h-28 overflow-auto space-y-0.5" data-testid="unmatched-folders-list">
        {preview.folders.map((f) => (
          <li key={f.folder} className="flex justify-between gap-2"><span className="truncate">{f.folder}{f.sf_number ? <span className="font-mono text-amber-700"> · {f.sf_number}</span> : null}</span><span className="text-amber-700 shrink-0">{f.file_count} file{f.file_count === 1 ? "" : "s"}</span></li>
        ))}
      </ul>
      <Button size="sm" variant="outline" onClick={create} disabled={busy} className="h-7 text-xs border-amber-400" data-testid="create-archive-cases-btn">
        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Archive className="w-3 h-3 mr-1" />}Create closed/archive cases for these folders
      </Button>
      <p className="text-[10px] text-amber-800">Creates a "Closed — Review Later" legal case per folder (SF number and director filled where a funded application matches) and attaches the documents. They stay out of the live tracker.</p>
    </div>
  );
}
